import type {
  ApplicationCondition,
  ConditionSection,
  ConditionStatus,
} from "src/types/conditions";
import type { DomainApplicationLenderConfiguration } from "@porchpass/api-client";

/**
 * Type guard to check if a lender is Porchpass.
 * Compares both lender_id and name fields (case-insensitive).
 *
 * @param lender - The lender to check
 * @returns true if the lender is Porchpass, false otherwise
 */
export const isPorchpassLender = (
  lender: DomainApplicationLenderConfiguration | undefined,
): lender is DomainApplicationLenderConfiguration => {
  if (!lender) return false;

  const lenderId = (lender as Record<string, unknown>).lender_id;
  const name = (lender as Record<string, unknown>).name;

  const lenderIdStr =
    typeof lenderId === "string" ? lenderId.toLowerCase() : "";
  const nameStr = typeof name === "string" ? name.toLowerCase() : "";

  return lenderIdStr === "porchpass" || nameStr === "porchpass";
};

/**
 * Derives the UI section for a condition based on its tag or category.
 * Maps condition metadata to one of three sections: customer, funding, or seller.
 *
 * @param condition - The condition to derive section for
 * @returns The derived ConditionSection ("customer", "funding", or "seller")
 *
 * @example
 * const section = deriveConditionSection({ tag: "customer_income" });
 * // Returns: "customer"
 */
export const deriveConditionSection = (
  condition: Record<string, unknown>,
): ConditionSection => {
  const tag = condition.tag;
  const category = condition.category;

  const tagStr = typeof tag === "string" ? tag.toLowerCase() : "";
  const categoryStr =
    typeof category === "string" ? category.toLowerCase() : "";

  const combinedStr = tagStr || categoryStr;

  if (combinedStr.includes("customer") || combinedStr.includes("borrower")) {
    return "customer";
  }
  if (combinedStr.includes("funding") || combinedStr.includes("lender")) {
    return "funding";
  }
  if (combinedStr.includes("seller") || combinedStr.includes("property")) {
    return "seller";
  }

  // Default to customer if we can't determine
  return "customer";
};

/**
 * Safely retrieves the condition name, ensuring a string is returned.
 *
 * @param condition - The condition to get the name from
 * @returns The condition name, or undefined if not present or not a string
 */
export const getConditionName = (
  condition: ApplicationCondition,
): string | undefined => {
  return typeof condition.name === "string" ? condition.name : undefined;
};

/**
 * Safely retrieves the condition status, ensuring a string is returned.
 *
 * @param condition - The condition to get the status from
 * @returns The condition status, or undefined if not present or not a string
 */
export const getConditionStatus = (
  condition: ApplicationCondition,
): ConditionStatus | undefined => {
  return typeof condition.status === "string" ? condition.status : undefined;
};

/**
 * Safely retrieves the condition description, ensuring a string is returned.
 *
 * @param condition - The condition to get the description from
 * @returns The condition description, or undefined if not present or not a string
 */
export const getConditionDescription = (
  condition: ApplicationCondition,
): string | undefined => {
  return typeof condition.description === "string"
    ? condition.description
    : undefined;
};

/**
 * Checks if a condition is in a pending status.
 *
 * A condition is considered pending if its status includes "pending" or "todo" (case-insensitive).
 *
 * @param condition - The condition to check
 * @returns true if the condition is pending, false otherwise
 */
export const isConditionPending = (
  condition: ApplicationCondition,
): boolean => {
  const status = getConditionStatus(condition)?.toLowerCase() || "";
  return status.includes("pending") || status.includes("todo");
};

/**
 * Safely retrieves the DigiFi TaskID from a condition.
 * This ID is required for uploading files to the specific condition in DigiFi.
 *
 * @param condition - The condition to get the task ID from
 * @returns The task ID, or undefined if not present or not a string
 */
export const getConditionTaskID = (
  condition: ApplicationCondition,
): string | undefined => {
  return typeof condition.task_id === "string" ? condition.task_id : undefined;
};
